<script lang="ts">
  import { onDestroy } from 'svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { MODE_INFO } from '../content/modes';
  import { rankLabel } from '../content/ranks';
  import { QueueConnection } from '../matchmaking/queue-connection.svelte';
  import { rankedProfileQueryOptions } from '../queries/ranked';
  import { getSession } from '../session/context';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import InlineMessage from '../ui/InlineMessage.svelte';
  import MatchFoundDialog from './MatchFoundDialog.svelte';

  /** Competitive home: the player's rank, the queue and the match-found prompt. */
  interface Props {
    /** Teach a new player the rules; resolves false when they dismiss. */
    explainRules: () => Promise<boolean>;
    /** Open the ranked room the Matchmaker created, or the unfinished one it pointed back to. */
    enterMatch: (roomCode: string) => void;
  }
  let { explainRules, enterMatch }: Props = $props();
  const session = getSession();

  const SEARCH_TICK_MS = 1_000;
  const queue = new QueueConnection({
    credential: () => session.getProfileCredential(),
    matched: (roomCode) => enterMatch(roomCode),
  });
  let destroyed = false;
  onDestroy(() => {
    destroyed = true;
    queue.dispose();
  });

  /** Rules come before the queue: the ranked join window leaves no time for them after a match. */
  async function findMatch(): Promise<void> {
    // The rules dialog outlives this screen, so a player can leave before answering it.
    if (!(await explainRules()) || destroyed) return;
    void queue.start();
  }

  const rank = createQuery(() =>
    rankedProfileQueryOptions($session.profileCredential, $session.profileGeneration),
  );
  const view = $derived(queue.view);
  let now = $state(Date.now());
  $effect(() => {
    if (view.kind !== 'searching') return;
    const timer = setInterval(() => (now = Date.now()), SEARCH_TICK_MS);
    return () => clearInterval(timer);
  });
  const waited = $derived(
    view.kind === 'searching' ? Math.max(0, Math.floor((now - view.since) / 1_000)) : 0,
  );
  const waitedLabel = $derived(`${Math.floor(waited / 60)}:${String(waited % 60).padStart(2, '0')}`);
</script>

<section id="screen-competitive" class="screen submenu">
  <h1>{MODE_INFO.competitive.title}</h1>
  <Card>
    <p class="rank" aria-live="polite">
      {#if rank.data}
        <strong>{rankLabel(rank.data.rank)}</strong>
        <small
          >{rank.data.wins} wins · {rank.data.losses} losses{rank.data.draws
            ? ` · ${rank.data.draws} draws`
            : ''}</small
        >
      {:else if rank.error}
        <small>{rank.error.message}</small>
      {:else}
        <small>Loading your rank…</small>
      {/if}
    </p>
    <p class="rules">{MODE_INFO.competitive.rules}</p>
    {#if view.kind === 'searching' || view.kind === 'proposal'}
      <p class="searching" role="status">Searching for an opponent… {waitedLabel}</p>
      <Button full onclick={() => queue.cancel()}>Cancel</Button>
    {:else if view.kind === 'connecting'}
      <Button variant="primary" size="big" full disabled aria-busy="true">Loading…</Button>
    {:else}
      {#if view.kind === 'refused'}
        <InlineMessage intent="error" message={view.message} />
        {#if view.roomCode}
          {@const roomCode = view.roomCode}
          <Button variant="primary" size="big" full onclick={() => enterMatch(roomCode)}
            >Return to Match</Button
          >
        {/if}
      {:else if view.kind === 'lost'}
        <InlineMessage intent="error" message="Connection lost. Queue again when you're ready." />
      {:else if view.notice}
        <InlineMessage intent="info" message={view.notice} />
      {/if}
      {#if view.kind !== 'refused' || !view.roomCode}
        <Button variant="primary" size="big" full onclick={() => void findMatch()}>Find a Match</Button>
      {/if}
    {/if}
  </Card>
</section>

<MatchFoundDialog
  open={view.kind === 'proposal'}
  opponent={view.kind === 'proposal' ? view.opponent : null}
  endAt={view.kind === 'proposal' ? view.endAt : 0}
  accepted={view.kind === 'proposal' && view.accepted}
  accept={() => queue.accept()}
  decline={() => queue.decline()}
/>

<style>
  .rank {
    display: grid;
    gap: 4px;
    margin: 0 0 12px;
    text-align: center;
  }
  .rank strong {
    font-size: 21px;
  }
  .rules {
    margin: 0 0 16px;
    font-size: 14px;
  }
  .searching {
    margin: 0 0 12px;
    text-align: center;
  }
</style>
